// Elección de la TALLA de un empleado al contratarlo: cuánta capacidad de
// trabajo recibe. Lo usan los TRES caminos de contratación (portal cliente,
// portal partner y panel admin) por el mismo motivo que EmployeeStoragePicker:
// si cada portal ofreciera lo suyo, acabarían ofreciendo cosas distintas.
//
// DECISIÓN DE PRODUCTO: aquí NO se enseñan cifras de CPU ni de memoria. Estos
// portales son comerciales —los mira quien contrata, no quien opera— y "1 Gi /
// 2 CPU" no le dice a nadie si su empleado va a ir bien. Se describe lo que
// significa, y el detalle queda detrás de la ayuda, en los mismos términos.
// Las cifras REALES viven en el deployer del cluster (services/sizes.py), que es
// quien las aplica, y se enseñan en la consola de WAI —que es donde se
// despliega—. Copiarlas aquí crearía dos verdades que se separarían en cuanto
// alguien subiera una talla.
import React from 'react';
import {
  Box, Card, CardActionArea, Chip, Collapse, FormHelperText, Link, Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';

export type EmployeeSize = 'estandar' | 'ampliada';

export const DEFAULT_EMPLOYEE_SIZE: EmployeeSize = 'estandar';

/** Normaliza cualquier cosa que llegue del backend a una talla pintable. No
 *  lanza: una talla desconocida (un backend más nuevo, un dato viejo) no puede
 *  dejar el diálogo en blanco — se cae a la estándar, igual que hace el
 *  deployer al desplegar. */
export const asSize = (value?: string | null): EmployeeSize =>
  value === 'ampliada' ? 'ampliada' : DEFAULT_EMPLOYEE_SIZE;

/** La talla de un empleado YA contratado, para su ficha/fila. Se pinta siempre
 *  —también la estándar— porque «no pone nada» no se lee como «es la estándar»,
 *  se lee como «no lo sé»: y aquí sí lo sabemos. */
export const EmployeeSizeChip: React.FC<{ size?: string | null }> = ({ size }) => {
  const { t } = useTranslation();
  const s = asSize(size);
  return (
    <Chip
      size="small"
      variant="outlined"
      color={s === 'ampliada' ? 'primary' : 'default'}
      label={s === 'ampliada'
        ? t('employeeSize.ampliada.label', 'Ampliada')
        : t('employeeSize.estandar.label', 'Estándar')}
      title={t('employeeSize.chipHelp',
        'Capacidad de trabajo del empleado. Para cambiarla hay que volver a desplegarlo.')}
    />
  );
};

interface Props {
  value: EmployeeSize;
  onChange: (next: EmployeeSize) => void;
  disabled?: boolean;
  /** Talla que viene marcada por defecto (la preferencia del partner). Cuando
   *  es distinta de la estándar se dice de dónde sale: si no, quien contrata ve
   *  una elección hecha por él que no hizo. */
  defaultFrom?: 'partner' | null;
  /** true = el empleado YA existe y se está cambiando su talla. Cambia el aviso
   *  de abajo: pasa de «se aplica al desplegar» a «hay que redesplegarlo», que
   *  es lo único que quien lo lee necesita decidir. */
  redeployWarning?: boolean;
}

const EmployeeSizePicker: React.FC<Props> = ({ value, onChange, disabled,
                                              defaultFrom, redeployWarning }) => {
  const { t } = useTranslation();
  const [showDetail, setShowDetail] = React.useState(false);

  const opciones: Array<{ id: EmployeeSize; label: string; summary: string; detail: string }> = [
    {
      id: 'estandar',
      label: t('employeeSize.estandar.label', 'Estándar'),
      summary: t('employeeSize.estandar.summary',
        'Para el trabajo del día a día: conversaciones, consultas y tareas de fondo con volumen normal.'),
      detail: t('employeeSize.estandar.detail',
        'Atiende una conversación a la vez con soltura y va haciendo su trabajo de fondo entre medias. Es la talla con la que funcionan hoy todos los empleados.')
    },
    {
      id: 'ampliada',
      label: t('employeeSize.ampliada.label', 'Ampliada'),
      summary: t('employeeSize.ampliada.summary',
        'Para empleados muy solicitados: varias conversaciones a la vez, trabajo autónomo frecuente o documentos grandes.'),
      detail: t('employeeSize.ampliada.detail',
        'Aproximadamente el cuádruple de capacidad de proceso y el doble de memoria. Se nota cuando varias personas le escriben a la vez, cuando trabaja solo a menudo o cuando maneja ficheros pesados.')
    }
  ];

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t('employeeSize.title', 'Capacidad del empleado')}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {opciones.map((o) => {
          const selected = value === o.id;
          return (
            <Card
              key={o.id}
              variant="outlined"
              sx={{
                flex: '1 1 240px',
                borderColor: selected ? 'primary.main' : 'divider',
                borderWidth: selected ? 2 : 1,
                opacity: disabled ? 0.6 : 1
              }}
            >
              <CardActionArea
                disabled={disabled}
                onClick={() => onChange(o.id)}
                sx={{ p: 2, height: '100%', alignItems: 'flex-start' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {o.label}
                  </Typography>
                  {o.id === DEFAULT_EMPLOYEE_SIZE && (
                    <Chip size="small" label={t('employeeSize.usual', 'La habitual')} />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {o.summary}
                </Typography>
                <Collapse in={showDetail}>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {o.detail}
                  </Typography>
                </Collapse>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>
      <FormHelperText component="div" sx={{ mt: 1 }}>
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={() => setShowDetail((v) => !v)}
          sx={{ fontSize: 'inherit' }}
        >
          {showDetail
            ? t('employeeSize.hideDetail', 'Ocultar qué incluye cada una')
            : t('employeeSize.showDetail', '¿Qué incluye cada una?')}
        </Link>
        {' — '}
        {redeployWarning
          ? t('employeeSize.changeWarning',
              'Cambiar la capacidad de un empleado que ya está en marcha exige volver a desplegarlo: se reinicia un momento y no pierde nada.')
          : t('employeeSize.applyNote',
              'Se aplica al desplegarlo. Puedes cambiarla más adelante volviendo a desplegar: el empleado se reinicia un momento y no pierde nada.')}
        {defaultFrom === 'partner' && value === DEFAULT_EMPLOYEE_SIZE && ' '}
        {defaultFrom === 'partner' && (
          <>{' '}{t('employeeSize.partnerDefault',
            'Viene marcada la que tienes configurada por defecto; puedes cambiarla para este empleado.')}</>
        )}
      </FormHelperText>
    </Box>
  );
};

export default EmployeeSizePicker;
